import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { QuoteGetResponse } from '@lio/api-types';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Drawer as MuiDrawer, Grid, List, ListItem, Theme, Box, SxProps } from '@mui/material';
import { IconButton } from '@components/iconButton';
import { ChevronLeft } from '@assets/svgs';
import { arraysOverlap } from '@utils/arrays';
import { Tooltip } from '@components/tooltip';
import { DrawerList } from '@pages/QuoteDetails/constants/DrawerList.enum';
import { FallbackSidebar } from '@components/errorBoundary/FallbackSidebar';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useDragResize } from '../../hooks';
import { WIDTH_CONSTANTS } from '@theme/widthConstants';
import { useLayoutStore } from '@store/LayoutStore';
import { getIsFeatureFlagEnabled, FEATURE_FLAG } from '@utils/featureFlags';

const initialSidebarWidth = 284;

interface IDrawer {
  items?: DrawerListItem[];
  toggleDrawer?: (arg: DrawerList | null) => void;
  defaultItem?: string;
}

export type DrawerListItem = {
  name: DrawerList | null;
  icon?: ReactNode;
  isShown?: (quote?: QuoteGetResponse) => boolean;
  content?: React.ReactNode;
  pageContent?: React.ReactNode;
  path?: string;
  roles: string[];
  sidebarItemSearchParamValue?: string;
  sidebarTabSearchParamValue?: string;
  isDisabled?: boolean;
};

const initialDrawerItemState: DrawerListItem = { roles: [], name: null };

// FUTURE TODO
// when todo in 'QuoteStore - LayoutSlice' replaces some of this we will greatly reduce the number of use-effects/logic in those
export const Drawer = ({ items, toggleDrawer, defaultItem }: IDrawer) => {
  const [roles, { isInternalUser }] = useStore((state) => [state.roles, state.userAuth], shallow);
  const [sidebarWidth, setSidebarWidth, isSidebarOpen, setIsSidebarOpen] = useLayoutStore(
    (state) => [state.sidebarWidth, state.setSidebarWidth, state.isSidebarOpen, state.setIsSidebarOpen],
    shallow,
  );

  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParamsInTheUrl] = useSearchParams();
  const hasSidebarItemSearchParam = searchParams.has('sidebarItem');
  const sidebarItemSelected = searchParams.get('sidebarItem');

  const hasPinnedItemSearchParam = searchParams.has('sidebarPinned');
  const pinnedItemSelected = searchParams.get('sidebarPinned');
  const initialState =
    defaultItem === undefined
      ? initialDrawerItemState
      : items?.filter((el: DrawerListItem) => el.path?.includes(defaultItem))[0];
  const [errorBoundaryKey, setErrorBoundaryKey] = useState(0);
  const [selectedOption, setSelectedOption] = useState<DrawerListItem | undefined>(initialState);
  const [pinnedOption, setPinnedOption] = useState<DrawerListItem | undefined>(initialState);

  const isDragEnabled = getIsFeatureFlagEnabled(FEATURE_FLAG.QUOTE_SIDEBAR_DRAG, { isInternalUser });
  const prevIsOpenRef = useRef(isSidebarOpen);
  const hasBeenOpenedRef = useRef(false);

  const totalSideBarWidth = isSidebarOpen
    ? sidebarWidth.reduce((accumulator, currentValue) => accumulator + currentValue, 0)
    : (sidebarWidth?.[1] ?? 0);

  const [draggingWidth, setDraggingWidth] = useState<number | null>(totalSideBarWidth);

  const { dragHandleProps } = useDragResize({
    minWidth: isSidebarOpen ? sidebarWidth?.length * initialSidebarWidth : initialSidebarWidth,
    maxWidth: Math.floor(window.innerWidth * 0.6),
    defaultWidth: isSidebarOpen ? totalSideBarWidth : (sidebarWidth?.[1] ?? 0),
    onWidthChange: setDraggingWidth,
    currentWidth: draggingWidth ?? (isSidebarOpen ? totalSideBarWidth : (sidebarWidth?.[1] ?? 0)),
    isEnabled: isDragEnabled,
    onDragEnd: (newTotalSideBarWidth) => {
      if (!isSidebarOpen) {
        // add all new width to the right side
        setSidebarWidth([sidebarWidth?.[0] ?? 0, newTotalSideBarWidth]);
        return;
      }

      const addToSidebar = (newTotalSideBarWidth - totalSideBarWidth) / sidebarWidth?.length;
      const newSidebarWidths = sidebarWidth?.map((w) => w + addToSidebar);

      // if left side is smaller than 250, then set it to 250 and take from the right side
      if (newSidebarWidths[0] < 250) {
        const difference = 250 - newSidebarWidths[0];
        newSidebarWidths[0] = 250;
        if (newSidebarWidths.length > 1) {
          newSidebarWidths[1] = newSidebarWidths[1] - difference;
        }
      }

      setSidebarWidth(newSidebarWidths);
    },
  });

  useEffect(() => {
    if (!prevIsOpenRef.current && isSidebarOpen) {
      if (!hasBeenOpenedRef.current) {
        hasBeenOpenedRef.current = true;
      }
    }
    prevIsOpenRef.current = isSidebarOpen;
  }, [isSidebarOpen, setSidebarWidth, sidebarWidth]);

  useEffect(() => {
    if (hasSidebarItemSearchParam) {
      if (sidebarItemSelected) {
        const drawerItem = items?.find((i) => i.sidebarItemSearchParamValue === sidebarItemSelected);
        if (drawerItem) {
          setDraggingWidth((sidebarWidth?.[0] ?? 0) + (sidebarWidth?.[1] ?? 0));
          setSelectedOption(drawerItem);
        }
      }
    } else {
      setDraggingWidth(sidebarWidth?.[1] ?? 0);
      setSelectedOption(initialState);
    }
  }, [sidebarItemSelected, searchParams, items, hasSidebarItemSearchParam, defaultItem, initialState, sidebarWidth]);

  useEffect(() => {
    if (hasPinnedItemSearchParam && isInternalUser) {
      if (pinnedItemSelected) {
        const drawerItem = items?.find((i) => i.sidebarItemSearchParamValue === pinnedItemSelected);
        if (drawerItem && drawerItem !== pinnedOption) {
          setPinnedOption(drawerItem);
          if (sidebarWidth.length === 1) {
            if (totalSideBarWidth + initialSidebarWidth > Math.floor(window.innerWidth * 0.6)) {
              // take height from the right side if the new total width is too big
              const difference = totalSideBarWidth + initialSidebarWidth - Math.floor(window.innerWidth * 0.6);
              const newRightWidth = sidebarWidth?.[0] - difference;
              setSidebarWidth([initialSidebarWidth, newRightWidth]);
              setDraggingWidth(newRightWidth + initialSidebarWidth);
            } else {
              setSidebarWidth([initialSidebarWidth, sidebarWidth?.[0]]);
              setDraggingWidth(sidebarWidth?.[0] + initialSidebarWidth);
            }
          }

          if (drawerItem === selectedOption) {
            // find the 'next item' in drawer and set that as the selected option
            const nextItem = items
              ?.filter((i) => i.roles.some((role) => roles.includes(role)))
              .find((i) => i.sidebarItemSearchParamValue !== pinnedItemSelected);
            setSelectedOption(nextItem);

            const updated = new URLSearchParams(searchParams);
            updated.set('sidebarItem', nextItem?.sidebarItemSearchParamValue ?? '');
            setSearchParamsInTheUrl(updated, { replace: true });
          }
        }
      }
    } else if (pinnedOption?.content) {
      if (sidebarWidth.length > 1) {
        //remove pinned option

        setSidebarWidth([sidebarWidth[0]]);
        setDraggingWidth(sidebarWidth[0]);
      }

      setPinnedOption(undefined);
    }
  }, [
    pinnedOption,
    pinnedItemSelected,
    searchParams,
    items,
    hasPinnedItemSearchParam,
    defaultItem,
    initialState,
    roles,
    selectedOption,
    setSidebarWidth,
    sidebarWidth,
    isInternalUser,
    setSearchParamsInTheUrl,
    totalSideBarWidth,
  ]);

  useEffect(() => {
    const isOpen = selectedOption?.content !== undefined;
    setIsSidebarOpen(isOpen);
  }, [selectedOption?.content, setIsSidebarOpen]);

  const handleClick = (newItem: DrawerListItem) => {
    setErrorBoundaryKey(errorBoundaryKey + 1);
    const { name, path } = newItem;

    setSelectedOption((prevState: DrawerListItem | undefined) => {
      if (prevState?.name === null || prevState?.name !== name) {
        return newItem;
      }

      return { roles: [], name: null };
    });

    if (path && tab && !path.includes(tab)) {
      navigate(path);
    }

    return toggleDrawer ? toggleDrawer(name) : null;
  };

  const sidebarRef = useRef<any>(null);

  const forceSidebarOpen = isSidebarOpen || !!pinnedOption?.content;
  const adjustmentWidth = draggingWidth ?? (isSidebarOpen ? totalSideBarWidth : (sidebarWidth?.[1] ?? 0));

  return (
    <Box
      id='secondary-menu'
      ref={sidebarRef}
      sx={{
        position: 'relative',
        width: forceSidebarOpen
          ? `${WIDTH_CONSTANTS.SIDEBAR_WIDTH + adjustmentWidth}px`
          : `${WIDTH_CONSTANTS.SIDEBAR_WIDTH}px`,
        minWidth: `${WIDTH_CONSTANTS.SIDEBAR_WIDTH}px`,
        display: 'flex',
        flexDirection: 'row',
        transition: 'width 0.2s',
        height: '100%',
      }}
    >
      {/* left permanent drawer */}
      <MuiDrawer
        variant='permanent'
        tabIndex={0}
        anchor='left'
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'drawerNavigation.main',
              top: 'unset !important',
              height: '100%',
              maxWidth: '100%',
              width: `${WIDTH_CONSTANTS.SIDEBAR_WIDTH}px`,
              borderRadius: '0',
              maxHeight: '100%',
              boxShadow: !forceSidebarOpen ? 'none' : 'boxShadow[3]',
              position: 'relative',
              left: 0,
              zIndex: 1,
              overflowX: 'hidden',
            },
          },
        }}
      >
        <List sx={{ margin: '0 auto' }}>
          {items
            ?.filter((i) => arraysOverlap(i.roles, roles))
            ?.filter((i) => !!i.icon)
            .map((item: DrawerListItem) => {
              const isSelected =
                selectedOption?.name === item.name || (item.path && location.pathname.includes(item.path));

              const isPinned = pinnedItemSelected === item.sidebarItemSearchParamValue;

              return (
                <ListItem
                  data-testid={item.name}
                  key={item.name}
                  disablePadding
                  sx={{ marginTop: '25px', justifyContent: 'center' }}
                >
                  <Tooltip placement='right' title={item.name || ''}>
                    <IconButton
                      variant='circular'
                      sx={
                        item.isDisabled
                          ? ({
                              color: (theme: Theme) => theme.palette.text.disabled,
                              backgroundColor: (theme: Theme) => `${theme.palette.background.main} !important`,
                              pointerEvents: 'none',
                            } as SxProps)
                          : isPinned
                            ? ({
                                pointerEvents: 'none',
                                color: 'button.outlined',
                                backgroundColor: (theme: Theme) => `${theme.palette.button.outlinedDark}60`,
                              } as SxProps)
                            : isSelected
                              ? ({
                                  color: 'button.outlined',
                                  backgroundColor: 'button.outlinedDark',
                                  '&:hover': {
                                    backgroundColor: (theme: Theme) => `${theme.palette.button.outlinedDark}90`,
                                  },
                                } as SxProps)
                              : {
                                  color: 'text.main',
                                }
                      }
                      onClick={() => {
                        if (isSelected && !selectedOption?.content) {
                          // if the page is already selected then do nothing if theres no content (ie no bar to 'collapse')
                          return;
                        }
                        if (item?.isDisabled) {
                          return;
                        }
                        handleClick(item);
                      }}
                    >
                      {' '}
                      {item.icon}
                    </IconButton>
                  </Tooltip>
                </ListItem>
              );
            })}
        </List>
        <Grid
          container
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 'auto',
            minHeight: '80px',
            gap: 2,
          }}
        >
          {selectedOption?.content && (
            <IconButton variant='circular' onClick={() => handleClick(selectedOption)}>
              <ChevronLeft />
            </IconButton>
          )}
        </Grid>
      </MuiDrawer>
      {/* the open drawer */}
      {forceSidebarOpen && (
        <ErrorBoundary key={errorBoundaryKey} FallbackComponent={FallbackSidebar}>
          <MuiDrawer
            tabIndex={0}
            id='secondaryDrawer'
            open={forceSidebarOpen}
            anchor='left'
            variant='persistent'
            sx={{
              backgroundColor: 'drawerNavigation.main',
              position: 'absolute',
              left: `${WIDTH_CONSTANTS.SIDEBAR_WIDTH}px`,
              top: 0,
              height: '100%',
              maxHeight: '100%',
              zIndex: 1300,

              overflowX: 'hidden',
            }}
            transitionDuration={{ appear: 500, enter: 500, exit: 0 }}
            slotProps={{
              paper: {
                sx: {
                  width: `${draggingWidth ? draggingWidth : selectedOption?.content ? totalSideBarWidth : sidebarWidth?.[0]}px`,
                  borderRadius: '0',
                  marginLeft: 0,
                  top: 0,
                  left: 0,
                  height: '100%',
                  maxHeight: '100%',
                  backgroundColor: 'drawerNavigation.main',
                  boxShadow: (theme: Theme) => theme.boxShadow[3],
                  justifyContent: 'start',
                  overflow: 'auto',
                  paddingBottom: 0,
                  position: 'relative',
                  zIndex: 1300,
                  overflowX: 'hidden',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },

                  msOverflowStyle: 'none',
                },
              },
            }}
          >
            {pinnedOption && pinnedOption.content && sidebarWidth.length > 1 ? (
              <Grid
                container
                sx={{
                  width: selectedOption?.content ? totalSideBarWidth : sidebarWidth?.[1],
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Grid
                  id='section'
                  sx={{
                    width: selectedOption?.content ? sidebarWidth[0] : 0,
                    height: '100%',
                    borderRight: selectedOption?.content ? '1px solid gray' : 'none',
                    borderColor: 'divider.main',
                  }}
                >
                  {selectedOption?.content}
                </Grid>
                <Grid id='pinned-section' sx={{ width: sidebarWidth[1], height: '100%' }}>
                  {pinnedOption?.content}
                </Grid>
              </Grid>
            ) : (
              selectedOption?.content
            )}

            {isDragEnabled && <Box {...dragHandleProps} />}
          </MuiDrawer>
        </ErrorBoundary>
      )}
    </Box>
  );
};
